export interface RedFlag {
  number: number;
  title: string;
  image: string;
  imagePosition: 'left' | 'right';
  content: string;
}

export interface BlogPost {
  id: string;
  slug: string;
  image: string;
  title: string;
  description: string;
  author: string;
  authorImage: string;
  date: string;
  link: string;
  introduction?: string[];
  redFlags?: RedFlag[];
  authorSignature?: string;
}

// Utility function to generate URL-friendly slugs from titles
export const generateSlug = (title: string): string => {
  return title
    .toLowerCase()
    .trim()
    .replace(/[^\w\s-]/g, '') // Remove special characters except hyphens
    .replace(/\s+/g, '-') // Replace spaces with hyphens
    .replace(/-+/g, '-') // Replace multiple hyphens with single hyphen
    .replace(/^-+|-+$/g, ''); // Remove leading/trailing hyphens
};

export const blogPosts: BlogPost[] = [
  {
    id: '1',
    slug: 'five-red-flags-to-avoid-in-junior-mining-stock-promotions',
    image: '/images/Blog1.webp',
    title: 'Five Red Flags to Avoid in Junior Mining Stock Promotions',
    description: "Resource juniors use promotions to bring their story to investors who wouldn't come across their name otherwise.",
    author: 'Rob Bruggeman',
    authorImage: '/images/RobertBruggeman.webp',
    date: '27 Nov 2025',
    link: '/blog/five-red-flags-to-avoid-in-junior-mining-stock-promotions',
    introduction: [
      "Resource juniors use promotions to bring their story to investors who wouldn’t come across their name otherwise. There’s nothing wrong with promotions—they are a tool public companies use to drive engagement with investors. But things can go terribly wrong if investors rely on promotions as their only source of information. By their nature, they are there to generate attention—sometimes to a fault.",
      "Here at <b>The Wealthy Miner</b>, we provide resource investors and speculators with unbiased research and market analysis. Today, we’re going to share with you some of the warning signs you may see next time a “sponsored research” email lands in your inbox."
    ],
    redFlags: [
      {
        number: 1,
        title: 'Promotion Presented as Objective Research',
        image: '/images/Blog1RedFlag1.webp',
        imagePosition: 'left',
        content: "Promotions are paid for by public companies. They hire marketers and copywriters to craft a compelling story and deliver it to the market in exchange for a fee. <b>The writers will focus on the positives and not mention any red flags.</b> The fees are usually paid in cash, but they may also be paid in the company's shares or options. <b>Importantly, the fees need to be disclosed…but only at the start of the agreement.</b><br/><br/><b>If a promotion never mentions its sponsor or the fees, beware.</b> The company doing it is playing dirty. It should be clear right away that what you're reading is sponsored content, with all the attached caveats. Read the disclaimer to understand who paid for what and how much."
      },
      {
        number: 2,
        title: 'Undisclosed Conflicts of Interest',
        image: '/images/Blog1RedFlag2.webp',
        imagePosition: 'right',
        content: "If a copywriter or a marketing company doing the promo was paid in shares, they are not unbiased. They want the article to generate as much buzz in the investment community as possible. There's a good chance this buzz will spark investor interest and increase the company's trading volume. Potentially, it will give its share price a boost. <b>Marketers who are paid in shares have an incentive to be as aggressive as possible. The higher the share price goes, the more they get paid.</b><br/><br/>If they don't disclose their stock holdings, they aren't being completely transparent. They may even get in trouble with regulators. Investors who trade in the shares of these promoted companies could get a phone call they didn't expect."
      },
      {
        number: 3,
        title: "Promises of 'Guaranteed' Returns",
        image: '/images/Blog1RedFlag3.webp',
        imagePosition: 'left',
        content: "If a promotional article promises that a specific stock is about to deliver, for example, a 10x return, run away. It's hard to predict future returns in general, and it's impossible to legally and consistently generate that level of gains. <b>The promoted company may have potential, but the article should be very careful talking about that AND the risks.</b> The only guaranteed returns in the market are risk-free government bonds. With stocks, let alone resource juniors, there's a lot of uncertainty and risk. That's what makes them attractive for risk-tolerant investors… but expecting guaranteed gains is a road to disaster.<br/><br/>This is another area that regulators watch. A company doing a promotion that promises returns can get an unexpected call as well, but often the regulators don't get involved. As an investor, watch out for these promises. They are a sign that the company running its promotional campaign may not be scrupulous."
      },
      {
        number: 4,
        title: 'Using Celebrity Endorsement',
        image: '/images/Blog1RedFlag4.webp',
        imagePosition: 'right',
        content: "If a promotion talks about a billionaire investor holding shares in this company, don't pay too much attention to it. First, regular investors don't have the same risk tolerance as the super-wealthy. Second, <b>ultra-high-net-worth celebrities almost certainly don't know what stocks they hold themselves.</b> They have teams of people doing research and putting together portfolios for them. These portfolios can hold hundreds of names. Yes, the company doing the promotion could be one of them, but you may never know on what conditions the celebrity invested in it.<br/><br/>He or she might have been an early seed-stage backer who got their shares for next to nothing. <b>Regular investors are in a different boat. They buy shares on the public market much later and at higher prices.</b> It's a different game. A game you can still be very good at, but competing with celebrities' family offices is another path to financial ruin."
      },
      {
        number: 5,
        title: 'Creating Urgency',
        image: '/images/Blog1RedFlag5.webp',
        imagePosition: 'left',
        content: "Always take your time to research a stock and find whether it fits your investment strategy or not. If a promotion says that there's an opportunity that will expire over the next 24 hours, beware. <b>There's no urgency in investing.</b> Do your due diligence, listen to experts such as our own Rob Bruggeman, who was one of Toronto's most respected analysts, and make your own conclusions. The biggest mistake you can make is not being late to an opportunity, but rushing into an investment that turns out to be a dud. Avoiding losers is as important as picking winners.<br/><br/>If a company doing the promotion is a good pick, the opportunity to invest won't go away in a day or a week. Even very successful investors rarely buy at the bottom or sell at the top. An extra 10% won't matter if you uncover a homerun investment opportunity. You win not by quickly jumping on the latest and hottest trend, but by being disciplined and consistent in your approach to stock picks and risk management. This idea doesn't sound as exciting as &quot;10x Your Wealth Tomorrow,&quot; but it also won't destroy your hard-earned wealth."
      }
    ],
    authorSignature: 'Good Investing,\nRob Bruggeman, CFA'
  },
  // Add more blog posts below:
  // {
  //   id: '2',
  //   slug: generateSlug('Your Blog Post Title'),
  //   image: '/images/your-blog-image.webp',
  //   title: 'Your Blog Post Title',
  //   description: 'Your blog post description here.',
  //   author: 'Rob Bruggeman',
  //   authorImage: '/images/RobertBruggeman.webp',
  //   date: '27 Nov 2025',
  //   link: `/blog/${generateSlug('Your Blog Post Title')}`
  // },
];

